import { cs } from '../../lib/currency';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import EnrollmentBillingTab from './enrollments/EnrollmentBillingTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/timezone';
import {
  ArrowLeft,
  User,
  Users,
  BookOpen,
  Calendar,
  DollarSign,
  Clock,
  Edit,
  Save,
  X,
  History,
  CreditCard,
  Percent,
  CheckCircle,
  XCircle,
  Pause,
  PlayCircle,
  Ban,
  Loader2,
  MoreVertical,
  PauseCircle,
  ArrowRightLeft,
  AlertTriangle,
  CalendarClock
} from 'lucide-react';

const EnrollmentDetail = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    program_id: '',
    class_ids: [],
    payment_frequency: 'monthly',
    monthly_fee: '',
    notes: '',
    start_date: '',
    end_date: '',
    next_billing_date: '',
    last_payment_date: '',
    last_payment_amount: '',
    payment_due_date: '',
    payment_status: '',
    status: '',
    is_prorated: false,
    prorated_amount: '',
    // iter278: discount + manual override on the Edit Enrollment page
    // (parity with the Edit Enrollment dialog from iter273). All three
    // fields are sent to PUT /admin/enrollments/{id}; the backend writes
    // a discount_usage audit row and recomputes monthly_fee.
    discount_id: '',
    discount_amount: '',
    discount_reason: '',
    final_monthly_fee: '',
  });
  const [discounts, setDiscounts] = useState([]);
  
  // Quick Actions state
  const [actionDialog, setActionDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(null); // 'pause', 'cancel', 'resume', 'move', 'schedule_cancel'
  const [actionForm, setActionForm] = useState({
    pause_start_date: '',
    resume_date: '',
    cancel_date: '',
    scheduled_cancel_date: '',
    reason: '',
    new_class_id: '',
    // iter300: admin-editable next billing date during pause. Auto-
    // recomputed from `existing_nbd + months_paused` whenever pause
    // start/resume dates change.
    next_billing_date: '',
    next_billing_date_touched: false,
    // Admin overrides on cancellation — see PlayerDetail.js for context.
    override_notice_period: false,
    override_credit: false,
  });
  const [creditCalculation, setCreditCalculation] = useState(null);
  const [calculatingCredit, setCalculatingCredit] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]);
  
  // iter278: normalise any of the date shapes the backend / Mongo may
  // return into the strict YYYY-MM-DD that <input type="date"> requires.
  // Accepts:
  //   • null / "" / undefined          → ""
  //   • "2026-02-01"                    → "2026-02-01"  (sliced)
  //   • "2026-02-01T00:00:00.000Z"      → "2026-02-01"  (sliced)
  //   • "2/1/2026" / "02/01/2026"       → "2026-02-01"  (US fallback)
  //   • Date / datetime objects (rare)  → ISO via toISOString
  // The old code did a flat `.slice(0, 10)`, which silently produced
  // invalid date strings (e.g. "2/1/2026") that Chrome's native date
  // input rejects → field renders empty even though data exists.
  const toDateInputValue = (v) => {
    if (!v && v !== 0) return '';
    // Date objects
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      return v.toISOString().slice(0, 10);
    }
    const s = String(v).trim();
    if (!s) return '';
    // Already ISO-prefixed (YYYY-MM-DD…)
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    // US format M/D/YYYY or MM/DD/YYYY (legacy free-text rows)
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const [, mm, dd, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    // Last-resort parse via Date — works for "Feb 1, 2026" etc.
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return '';
  };

  // iter304: auto-recompute the post-pause `next_billing_date` from
  // `existing_nbd + months_paused` (calendar-month shift, rounded from
  // the day-span). User spec: "next bill 8/1, paused 1 month → 9/1".
  // The admin can override the auto-fill via the inline input — the
  // override sticks for the rest of the dialog session.
  //
  // iter304 fix: previous implementation used `new Date(str)` +
  // `.setMonth()` + `.toISOString()` which is bitten by timezone math
  // (e.g. Aug 1 UTC parsed → Jul 31 19:00 EDT → +2 months → Oct 1
  // 19:00 EDT → toISOString = Oct 2 UTC). User reported: "8/1 paused
  // 5/31→8/1 (2mo) gave 10/02 instead of 10/01". Switched to pure
  // YYYY-MM-DD string arithmetic — no Date object, no timezone bias.
  const computedPostPauseNbd = useMemo(() => {
    if (!actionForm.pause_start_date || !actionForm.resume_date) return '';
    const currentNbd = data?.enrollment?.next_billing_date;
    if (!currentNbd) return '';
    const startStr = toDateInputValue(actionForm.pause_start_date);
    const endStr = toDateInputValue(actionForm.resume_date);
    if (!startStr || !endStr) return '';
    // Day-count via UTC-noon timestamps so DST swings (which never
    // happen at noon UTC) can't shift the diff.
    const startTs = Date.UTC(
      Number(startStr.slice(0, 4)),
      Number(startStr.slice(5, 7)) - 1,
      Number(startStr.slice(8, 10)),
      12, 0, 0,
    );
    const endTs = Date.UTC(
      Number(endStr.slice(0, 4)),
      Number(endStr.slice(5, 7)) - 1,
      Number(endStr.slice(8, 10)),
      12, 0, 0,
    );
    const pauseDays = Math.round((endTs - startTs) / 86_400_000);
    if (pauseDays <= 0) return '';
    const monthsPaused = Math.max(1, Math.round(pauseDays / 30));

    const nbdStr = toDateInputValue(currentNbd);
    if (!nbdStr || nbdStr.length < 10) return '';
    const y = Number(nbdStr.slice(0, 4));
    const m = Number(nbdStr.slice(5, 7));   // 1-12
    const d = Number(nbdStr.slice(8, 10));  // 1-31
    // Pure month arithmetic. If the source day overflows the target
    // month (e.g. Aug 31 + 1mo → Sep 31 → clamp to Sep 30), clamp to
    // the last day of the target month — matches the SQL-style
    // `add_months` semantics admins expect.
    const totalIdx = (y * 12 + (m - 1)) + monthsPaused;
    const ty = Math.floor(totalIdx / 12);
    const tm = (totalIdx % 12) + 1; // 1-12
    const lastDayOfTargetMonth = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
    const td = Math.min(d, lastDayOfTargetMonth);
    const pad = (n) => String(n).padStart(2, '0');
    return `${ty}-${pad(tm)}-${pad(td)}`;
  }, [actionForm.pause_start_date, actionForm.resume_date, data?.enrollment?.next_billing_date]);

  // Auto-fill the editable next_billing_date input from the computed
  // value whenever the source dates change — unless the admin has
  // explicitly edited the field.
  useEffect(() => {
    if (actionForm.next_billing_date_touched) return;
    if (!computedPostPauseNbd) return;
    setActionForm((prev) =>
      prev.next_billing_date === computedPostPauseNbd
        ? prev
        : { ...prev, next_billing_date: computedPostPauseNbd },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedPostPauseNbd, actionForm.next_billing_date_touched]);

  // iter334ba — Auto-calculate pause credit whenever pause_start_date
  // changes (debounced). Backdating a pause into a paid month should
  // immediately show the admin how much credit will be issued, without
  // requiring them to click the "Calculate Credit" button. Skips fetch
  // when the modal isn't open on the pause action, or when the field
  // is blank.
  useEffect(() => {
    if (currentAction !== 'pause') return;
    if (!actionForm.pause_start_date) {
      setCreditCalculation(null);
      return;
    }
    const t = setTimeout(() => {
      calculatePauseCredit();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionForm.pause_start_date, currentAction]);



  useEffect(() => {
    fetchEnrollmentDetail();
    fetchProgramsAndClasses();
  }, [enrollmentId]);
  
  const fetchEnrollmentDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/enrollments/${enrollmentId}`);
      setData(response.data);
      
      // Initialize edit form with current values
      const e = response.data.enrollment;
      setEditForm({
        program_id: response.data.program?.id || '',
        class_ids: response.data.classes?.map(c => c.id) || [],
        payment_frequency: e.payment_frequency || 'monthly',
        // iter278: show the *base* fee in the input — if a discount is
        // active, monthly_fee is already post-discount, so we surface
        // original_monthly_fee. Keeps the math intuitive (admin edits
        // the base, the discount layer is applied below).
        monthly_fee: e.original_monthly_fee ?? e.monthly_fee ?? '',
        notes: e.notes || '',
        // iter278: defensive date parsing — see toDateInputValue() above.
        start_date: toDateInputValue(e.start_date),
        end_date: toDateInputValue(e.end_date),
        next_billing_date: toDateInputValue(e.next_billing_date),
        last_payment_date: toDateInputValue(e.last_payment_date),
        last_payment_amount: e.last_payment_amount ?? '',
        payment_due_date: toDateInputValue(e.payment_due_date),
        payment_status: e.payment_status || '',
        status: e.status || '',
        is_prorated: !!e.is_prorated,
        prorated_amount: e.prorated_amount ?? '',
        // iter278: discount seeding (matches Edit Enrollment dialog).
        discount_id: e.discount_id || '',
        discount_amount: e.discount_amount != null && !e.discount_id ? e.discount_amount : '',
        discount_reason: e.discount_reason || '',
        final_monthly_fee: '',
      });
    } catch (error) {
      toast.error('Failed to fetch enrollment details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProgramsAndClasses = async () => {
    try {
      const [programsRes, classesRes, discountsRes] = await Promise.all([
        api.get('/programs'),
        api.get('/classes'),
        // iter279: match the Enrollments page / dialog exactly — call
        // /discounts (not /admin/discounts) and filter by is_active. The
        // admin variant was returning a narrower (sometimes empty) set
        // depending on backend filtering, which is why the dropdown on
        // this page showed only "No discount".
        api.get('/discounts').catch(() => ({ data: [] })),
      ]);
      setPrograms(programsRes.data);
      setClasses(classesRes.data);
      const discList = Array.isArray(discountsRes.data) ? discountsRes.data : [];
      setDiscounts(discList.filter((d) => d.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch programs/classes:', error);
    }
  };
  
  // Quick Action handlers
  const openActionDialog = (action) => {
    setCurrentAction(action);
    setCreditCalculation(null);
    
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    setActionForm({
      pause_start_date: today,
      // iter334ba — Do NOT pre-populate resume_date. Empty default
      // lets admin choose: leave blank for indefinite pause, or fill
      // in a future/past date. Backdating is intentionally allowed —
      // credit is auto-calculated based on the pause start date.
      resume_date: '',
      cancel_date: '',
      reason: '',
      new_class_id: '',
      next_billing_date: '',
      next_billing_date_touched: false,
      // iter334bb — pause/cancel credit override checkboxes.
      override_credit: false,
      override_notice_period: false,
    });
    
    // Get available classes for move action
    if (action === 'move' && data?.program?.id) {
      const programClasses = classes.filter(c => 
        c.program_id === data.program.id && 
        c.is_active !== false &&
        !data.classes?.some(ec => ec.id === c.id)
      );
      setAvailableClasses(programClasses);
    }
    
    setActionDialog(true);
    
    // Auto-calculate credit for cancel
    if (action === 'cancel') {
      calculateCancelCredit();
    }
  };
  
  const calculatePauseCredit = async () => {
    if (!actionForm.pause_start_date) return;
    
    setCalculatingCredit(true);
    try {
      const response = await api.get(`/enrollments/${enrollmentId}/calculate-pause-credit?pause_date=${actionForm.pause_start_date}`);
      setCreditCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };
  
  const calculateCancelCredit = async (cancelDate = null) => {
    setCalculatingCredit(true);
    try {
      let url = `/enrollments/${enrollmentId}/calculate-cancel-credit`;
      if (cancelDate) {
        url += `?cancel_date=${cancelDate}`;
      }
      const response = await api.get(url);
      setCreditCalculation(response.data);
      
      // Set the earliest cancel date as default if not set
      if (!actionForm.cancel_date && response.data.earliest_cancel_date) {
        setActionForm(prev => ({
          ...prev,
          cancel_date: response.data.earliest_cancel_date
        }));
      }
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };
  
  const handleQuickAction = async () => {
    setProcessingAction(true);
    try {
      let response;
      
      if (currentAction === 'pause') {
        if (!actionForm.pause_start_date) {
          toast.error('Please provide a pause start date');
          setProcessingAction(false);
          return;
        }
        // iter334ba — resume_date is optional. When blank, backend
        // treats it as an indefinite pause (stashes NBD, admin
        // resumes manually via /admin-resume).
        response = await api.post(`/enrollments/${enrollmentId}/admin-pause`, {
          pause_start_date: actionForm.pause_start_date,
          resume_date: actionForm.resume_date || null,
          reason: actionForm.reason,
          // iter300: forward the (possibly admin-overridden) post-pause
          // next billing date so the server doesn't recompute from
          // scratch and clobber the admin's choice.
          next_billing_date: actionForm.next_billing_date || null,
          // iter334bb — Admin opts to skip the pro-rated credit.
          override_credit: !!actionForm.override_credit,
        });
      } else if (currentAction === 'cancel') {
        if (!(actionForm.reason || '').trim()) {
          toast.error('Reason is required to cancel an enrollment');
          setProcessingAction(false);
          return;
        }
        response = await api.post(`/enrollments/${enrollmentId}/admin-cancel`, {
          cancel_date: actionForm.cancel_date || null,
          reason: actionForm.reason,
          override_notice_period: !!actionForm.override_notice_period,
          override_credit: !!actionForm.override_credit,
        });
      } else if (currentAction === 'resume') {
        response = await api.post(`/enrollments/${enrollmentId}/admin-resume`, {
          resume_date: actionForm.resume_date || null,
          reason: actionForm.reason
        });
      } else if (currentAction === 'move') {
        if (!actionForm.new_class_id) {
          toast.error('Please select a class to move to');
          setProcessingAction(false);
          return;
        }
        response = await api.post(`/enrollments/${enrollmentId}/move`, {
          new_class_id: actionForm.new_class_id,
          reason: actionForm.reason
        });
      } else if (currentAction === 'schedule_cancel') {
        if (!actionForm.scheduled_cancel_date) {
          toast.error('Please select a future cancellation date');
          setProcessingAction(false);
          return;
        }
        response = await api.put(`/admin/enrollments/${enrollmentId}/schedule-cancel`, {
          scheduled_cancel_date: actionForm.scheduled_cancel_date,
          notes: actionForm.reason
        });
      } else if (currentAction === 'remove_schedule_cancel') {
        response = await api.delete(`/admin/enrollments/${enrollmentId}/schedule-cancel`);
      }
      
      toast.success(response?.data?.message || 'Action completed successfully');
      setActionDialog(false);
      fetchEnrollmentDetail(); // Refresh data
      
    } catch (error) {
      console.error('Failed to process action:', error);
      toast.error(error.response?.data?.detail || 'Failed to process action');
    } finally {
      setProcessingAction(false);
    }
  };
  
  const canPause = data?.enrollment?.status === 'current' || data?.enrollment?.status === 'active' || data?.enrollment?.status === 'approved';
  const canResume = data?.enrollment?.status === 'paused';
  const canCancel = data?.enrollment?.status === 'current' || data?.enrollment?.status === 'active' || data?.enrollment?.status === 'paused' || data?.enrollment?.status === 'approved';
  const canMove = data?.enrollment?.status === 'current' || data?.enrollment?.status === 'active' || data?.enrollment?.status === 'approved';
  const canScheduleCancel = (data?.enrollment?.status === 'current' || data?.enrollment?.status === 'active' || data?.enrollment?.status === 'paused' || data?.enrollment?.status === 'approved') && !data?.enrollment?.scheduled_cancel_date;
  const hasScheduledCancel = !!data?.enrollment?.scheduled_cancel_date;
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        monthly_fee: editForm.monthly_fee !== '' ? parseFloat(editForm.monthly_fee) : null,
        payment_frequency: editForm.payment_frequency,
        notes: editForm.notes,
        // iter308z — Send program_id so admin can repoint an enrollment
        // to a different program from EnrollmentDetail's edit mode.
        // The backend validates the program exists + that any sent
        // class_ids belong to it.
        program_id: editForm.program_id || null,
        class_ids: editForm.class_ids,
        // Date reconciliation fields (all YYYY-MM-DD, empty string clears).
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        next_billing_date: editForm.next_billing_date || null,
        last_payment_date: editForm.last_payment_date || null,
        last_payment_amount: editForm.last_payment_amount !== '' ? parseFloat(editForm.last_payment_amount) : null,
        payment_due_date: editForm.payment_due_date || null,
        // Status fields
        payment_status: editForm.payment_status || null,
        status: editForm.status || null,
        // Proration
        is_prorated: !!editForm.is_prorated,
        prorated_amount: editForm.prorated_amount !== '' ? parseFloat(editForm.prorated_amount) : null,
        // iter278: discount fields — same shape the Edit Enrollment dialog
        // and the backend `update_enrollment` handler expect. Sending an
        // empty discount_id clears any active catalog discount.
        discount_id: editForm.discount_id || '',
        discount_amount: editForm.discount_amount !== '' && !editForm.discount_id
          ? parseFloat(editForm.discount_amount)
          : null,
        discount_reason: editForm.discount_reason || null,
        // iter307cr — Override field is now CYCLE-LEVEL for non-monthly
        // enrollments (clearer for admins). Convert back to per-month
        // before sending so the backend contract (per-month
        // `monthly_fee` storage) is preserved end-to-end.
        final_monthly_fee: (() => {
          if (editForm.final_monthly_fee === '' || editForm.final_monthly_fee == null) return null;
          const v = parseFloat(editForm.final_monthly_fee);
          if (Number.isNaN(v)) return null;
          const mult = Number(enrollment?.cycle_multiplier ?? 1) || 1;
          return mult > 1 ? Math.round((v / mult) * 100) / 100 : v;
        })(),
      };
      await api.put(`/admin/enrollments/${enrollmentId}`, payload);
      toast.success('Enrollment updated successfully');
      setIsEditing(false);
      fetchEnrollmentDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enrollment');
    } finally {
      setSaving(false);
    }
  };
  
  const handleClassToggle = (classId) => {
    setEditForm(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-700',
      approved: 'bg-blue-100 text-blue-700',
      current: 'bg-green-100 text-green-700',
      active: 'bg-green-100 text-green-700',
      paused: 'bg-amber-100 text-amber-700',
      canceled: 'bg-red-100 text-red-700',
      cancelled: 'bg-red-100 text-red-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };
  
  const _getPaymentStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      overdue: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateOnly(dateStr, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' });
  };
  
  const formatActionName = (action) => {
    const actionMap = {
      'approved': 'Approved',
      'rejected': 'Rejected',
      'paused': 'Paused',
      'resumed': 'Resumed',
      'auto_resumed': 'Auto-Resumed',
      'canceled': 'Canceled',
      'edited': 'Edited',
      'pause_updated': 'Pause Updated',
      'admin_moved': 'Moved',
      'payment_received': 'Payment Received',
      'created': 'Created'
    };
    return actionMap[action] || action;
  };
  
  const getActionIcon = (action) => {
    const icons = {
      'approved': <CheckCircle className="h-4 w-4 text-green-600" />,
      'rejected': <XCircle className="h-4 w-4 text-red-600" />,
      'paused': <Pause className="h-4 w-4 text-amber-600" />,
      'resumed': <PlayCircle className="h-4 w-4 text-blue-600" />,
      'auto_resumed': <PlayCircle className="h-4 w-4 text-green-600" />,
      'canceled': <Ban className="h-4 w-4 text-red-600" />,
      'edited': <Edit className="h-4 w-4 text-purple-600" />,
      'payment_received': <DollarSign className="h-4 w-4 text-green-600" />
    };
    return icons[action] || <History className="h-4 w-4 text-slate-600" />;
  };
  
  const filteredClasses = editForm.program_id
    ? classes.filter(c => c.program_id === editForm.program_id && c.is_active !== false)
    : [];
  
  if (loading) {
    return (
      <DashboardLayout title="Enrollment Details">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }
  
  if (!data) {
    return (
      <DashboardLayout title="Enrollment Details">
        <div className="text-center py-12">
          <p className="text-slate-500">Enrollment not found</p>
          <Button onClick={() => navigate('/admin/enrollments')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Enrollments
          </Button>
        </div>
      </DashboardLayout>
    );
  }
  
  const { enrollment, player, parent, program, classes: enrollmentClasses, subscription, history, requests } = data;
  
  return (
    <DashboardLayout title="Enrollment Details">
      <div className="space-y-6" data-testid="enrollment-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/enrollments')}
              data-testid="back-to-enrollments-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Enrollment {enrollment.numeric_id || enrollment.id.slice(0, 8)}
              </h1>
              <p className="text-sm text-slate-500">
                {player?.full_name} - {program?.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(enrollment.status)}
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(false)}
                  data-testid="cancel-edit-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  data-testid="save-enrollment-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-enrollment-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                
                {/* Quick Actions Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" data-testid="quick-actions-btn">
                      <MoreVertical className="h-4 w-4 mr-2" />
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {canPause && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('pause')}
                        className="text-amber-600"
                        data-testid="action-pause"
                      >
                        <PauseCircle className="h-4 w-4 mr-2" />
                        Pause Enrollment
                      </DropdownMenuItem>
                    )}
                    {canResume && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('resume')}
                        className="text-green-600"
                        data-testid="action-resume"
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Resume Enrollment
                      </DropdownMenuItem>
                    )}
                    {canMove && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('move')}
                        className="text-blue-600"
                        data-testid="action-move"
                      >
                        <ArrowRightLeft className="h-4 w-4 mr-2" />
                        Move to Another Class
                      </DropdownMenuItem>
                    )}
                    {(canPause || canResume || canMove) && canCancel && <DropdownMenuSeparator />}
                    {canScheduleCancel && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('schedule_cancel')}
                        className="text-orange-600"
                        data-testid="action-schedule-cancel"
                      >
                        <CalendarClock className="h-4 w-4 mr-2" />
                        Schedule Future Cancel
                      </DropdownMenuItem>
                    )}
                    {hasScheduledCancel && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('remove_schedule_cancel')}
                        className="text-slate-600"
                        data-testid="action-remove-schedule-cancel"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Scheduled Cancel
                      </DropdownMenuItem>
                    )}
                    {canCancel && (
                      <DropdownMenuItem 
                        onClick={() => openActionDialog('cancel')}
                        className="text-red-600"
                        data-testid="action-cancel"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Now
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
        
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              History ({(history?.length || 0) + (requests?.length || 0)})
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Player Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="h-5 w-5 text-blue-600" />
                    Player
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Name</span>
                    <Link 
                      to={`/admin/players/${player?.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {player?.full_name}
                    </Link>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">ID</span>
                    <span className="font-mono text-sm">{player?.numeric_id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Date of Birth</span>
                    <span>{formatDate(player?.date_of_birth)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Skill Level</span>
                    <Badge variant="outline" className="capitalize">{player?.skill_level}</Badge>
                  </div>
                </CardContent>
              </Card>
              
              {/* Parent Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="h-5 w-5 text-emerald-600" />
                    Parent
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Name</span>
                    <Link 
                      to={`/admin/parents/${parent?.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {parent?.name}
                    </Link>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">ID</span>
                    <span className="font-mono text-sm">{parent?.numeric_id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Email</span>
                    <a href={`mailto:${parent?.email}`} className="text-blue-600 hover:underline text-sm">
                      {parent?.email}
                    </a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Phone</span>
                    <a href={`tel:${parent?.phone}`} className="text-blue-600 hover:underline">
                      {parent?.phone || '-'}
                    </a>
                  </div>
                </CardContent>
              </Card>
              
              {/* Program Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="h-5 w-5 text-purple-600" />
                    Program
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Label>Program</Label>
                      <Select
                        value={editForm.program_id}
                        onValueChange={(v) => setEditForm({ ...editForm, program_id: v, class_ids: [] })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select program" />
                        </SelectTrigger>
                        <SelectContent>
                          {programs.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Program</span>
                        <span className="font-medium">{program?.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">ID</span>
                        <span className="font-mono text-sm">{program?.numeric_id}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Base Price</span>
                        <span>{cs()}{program?.price?.toFixed(2)}/mo</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              
              {/* Classes Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="h-5 w-5 text-amber-600" />
                    Classes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div className="space-y-2">
                      <Label>Select Classes</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                        {filteredClasses.length === 0 ? (
                          <p className="text-sm text-slate-500">Select a program first</p>
                        ) : (
                          filteredClasses.map(cls => (
                            <label 
                              key={cls.id} 
                              className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={editForm.class_ids.includes(cls.id)}
                                onChange={() => handleClassToggle(cls.id)}
                                className="rounded"
                              />
                              <span>{cls.name}</span>
                              <span className="text-xs text-slate-400 ml-auto">
                                {cls.schedule_day || cls.day_of_week} {cls.start_time}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {enrollmentClasses?.length > 0 ? (
                        enrollmentClasses.map((cls, idx) => (
                          <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                            <div className="font-medium">{cls.name}</div>
                            <div className="text-sm text-slate-500 mt-1">
                              {cls.schedule_day} {cls.start_time && `at ${cls.start_time}`}
                              {cls.end_time && ` - ${cls.end_time}`}
                            </div>
                            {cls.coach_name && (
                              <div className="text-xs text-slate-400 mt-1">
                                Coach: {cls.coach_name}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">No classes assigned</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Dates Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="h-5 w-5 text-slate-600" />
                  Important Dates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">Created</div>
                    <div className="font-medium">{formatDate(enrollment.created_at)}</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-green-600 mb-1">Approved</div>
                    <div className="font-medium text-green-700">{formatDate(enrollment.approved_at)}</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-600 mb-1">Start Date</div>
                    <div className="font-medium text-blue-700">{formatDate(enrollment.start_date)}</div>
                  </div>
                  {enrollment.pause_date && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <div className="text-xs text-amber-600 mb-1">Paused</div>
                      <div className="font-medium text-amber-700">{formatDate(enrollment.pause_date)}</div>
                    </div>
                  )}
                  {enrollment.auto_resume_date && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-green-600 mb-1">Auto-Resume</div>
                      <div className="font-medium text-green-700">{formatDate(enrollment.auto_resume_date)}</div>
                    </div>
                  )}
                  {enrollment.scheduled_cancel_date && (
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="text-xs text-orange-600 mb-1 flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        Scheduled Cancellation
                      </div>
                      <div className="font-medium text-orange-700">{formatDate(enrollment.scheduled_cancel_date)}</div>
                      <p className="text-xs text-orange-500 mt-1">Billing stops on this date</p>
                    </div>
                  )}
                  {enrollment.cancel_date && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="text-xs text-red-600 mb-1">Cancelled</div>
                      <div className="font-medium text-red-700">{formatDate(enrollment.cancel_date)}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* iter300: Billing cards merged from former Billing tab.
                User spec: billing as its own box below Important Dates,
                discounts next to it. Notes moved below to keep the
                billing summary near the top. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Payment Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      {(() => {
                        // iter307cs — Program defines three PER-MONTH
                        // pricing tiers (monthly/quarterly/biannual);
                        // changing the frequency in Edit pulls the
                        // matching per-month rate, but the field stays
                        // editable so admins can override.
                        const findProg = () =>
                          programs.find(p => p.id === editForm.program_id) || program;
                        const perMonthForFreq = (prog, freq) => {
                          if (!prog) return 0;
                          if (freq === 'quarterly' && prog.quarterly_price) return Number(prog.quarterly_price);
                          if (freq === 'biannual' && prog.biannual_price) return Number(prog.biannual_price);
                          return Number(prog.price || 0);
                        };
                        const handleFreqChange = (v) => {
                          const prog = findProg();
                          const newFee = perMonthForFreq(prog, v);
                          setEditForm({
                            ...editForm,
                            payment_frequency: v,
                            monthly_fee: newFee > 0 ? String(newFee) : editForm.monthly_fee,
                          });
                        };
                        return (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>Payment Frequency</Label>
                              <Select
                                value={editForm.payment_frequency}
                                onValueChange={handleFreqChange}
                              >
                                <SelectTrigger data-testid="ed-frequency"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                  <SelectItem value="quarterly">Quarterly</SelectItem>
                                  <SelectItem value="biannual">Bi-Annual</SelectItem>
                                </SelectContent>
                              </Select>
                              {(() => {
                                const prog = findProg();
                                if (!prog) return null;
                                const tier = perMonthForFreq(prog, editForm.payment_frequency);
                                return tier > 0 ? (
                                  <p className="text-[11px] text-slate-500" data-testid="ed-freq-program-tier">
                                    Program rate: {cs()}{tier.toFixed(2)}/month
                                  </p>
                                ) : null;
                              })()}
                            </div>
                            <div className="space-y-2">
                              <Label>Monthly Base Fee ($)
                                <span className="ml-1 text-[10px] text-slate-400 font-normal">— per month, editable override</span>
                              </Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editForm.monthly_fee}
                                onChange={(e) => setEditForm({ ...editForm, monthly_fee: e.target.value })}
                                placeholder="0.00"
                                data-testid="ed-monthly-fee"
                              />
                            </div>
                          </div>
                        );
                      })()}

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={editForm.start_date || ''}
                            onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                            data-testid="ed-start-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={editForm.end_date || ''}
                            onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                            data-testid="ed-end-date"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Next Bill Date</Label>
                          <Input
                            type="date"
                            value={editForm.next_billing_date || ''}
                            onChange={(e) => setEditForm({ ...editForm, next_billing_date: e.target.value })}
                            data-testid="ed-next-bill-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Payment Due Date</Label>
                          <Input
                            type="date"
                            value={editForm.payment_due_date || ''}
                            onChange={(e) => setEditForm({ ...editForm, payment_due_date: e.target.value })}
                            data-testid="ed-due-date"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Last Payment Date</Label>
                          <Input
                            type="date"
                            value={editForm.last_payment_date || ''}
                            onChange={(e) => setEditForm({ ...editForm, last_payment_date: e.target.value })}
                            data-testid="ed-last-payment-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Payment Amount ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.last_payment_amount ?? ''}
                            onChange={(e) => setEditForm({ ...editForm, last_payment_amount: e.target.value })}
                            placeholder="Optional"
                            data-testid="ed-last-payment-amount"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Payment Status</Label>
                          <Select
                            value={editForm.payment_status || 'pending'}
                            onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}
                          >
                            <SelectTrigger data-testid="ed-payment-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="overdue">Overdue</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="void">Void</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Enrollment Status</Label>
                          <Select
                            value={editForm.status || 'current'}
                            onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                          >
                            <SelectTrigger data-testid="ed-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="current">Current</SelectItem>
                              <SelectItem value="pause_requested">Pause Requested</SelectItem>
                              <SelectItem value="paused">Paused</SelectItem>
                              <SelectItem value="canceled">Canceled</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 items-end">
                        {/* iter334bw.3 — Removed manual `is_prorated` +
                            `prorated_amount` edit controls. Proration is
                            now derived by the system from start_date
                            at enrollment approval AND from paid amount
                            vs monthly_fee at first-cycle billing.
                            Admins should not override this post-hoc.
                            The read-only display below (line ~1281 /
                            1493) still shows the current prorated state
                            for context. */}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* iter307cr — Payment Details = CYCLE-LEVEL $$$
                          numbers. Discount card on the right carries the
                          discount metadata (badge, type, reason) without
                          duplicating dollar amounts. Source of truth for
                          all amounts: backend cycle_* fields computed
                          via `_compute_cycle_pricing` in admin.py. */}
                      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="enrollment-pricing-card">
                        {(() => {
                          const baseFee = Number(enrollment.base_monthly_fee ?? enrollment.original_monthly_fee ?? enrollment.monthly_fee ?? 0);
                          const cycleTotal = Number(enrollment.cycle_total_before_discount ?? (baseFee * Number(enrollment.cycle_multiplier ?? 1)));
                          const cycleDisc = Number(enrollment.cycle_discount_amount ?? 0);
                          const cycleAmtDue = Number(enrollment.cycle_amount_due ?? Math.max(0, cycleTotal - cycleDisc));
                          const cycleLabel = enrollment.cycle_label || (Number(enrollment.cycle_multiplier) > 1 ? `every ${enrollment.cycle_multiplier} months` : 'every month');
                          const hasDiscount = cycleDisc > 0 || baseFee !== Number(enrollment.monthly_fee ?? 0);
                          return (
                            <>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <div className="space-y-1">
                                  <p className="text-xs text-slate-500">Payment Frequency</p>
                                  <div className="flex items-baseline gap-2">
                                    <Badge variant="outline" className="capitalize" data-testid="view-frequency">{enrollment.payment_frequency || 'monthly'}</Badge>
                                    <span className="text-[11px] text-slate-500">{cycleLabel}</span>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-xs text-slate-500">Program Base Fee</p>
                                  <p className="font-semibold text-base" data-testid="view-base-monthly-fee">
                                    {cs()}{baseFee.toFixed(2)}
                                    <span className="text-xs text-slate-400 font-normal"> / month</span>
                                  </p>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-xs text-slate-500">Cycle Total (pre-discount)</p>
                                  <p className="text-sm font-medium" data-testid="view-cycle-total">{cs()}{cycleTotal.toFixed(2)}</p>
                                </div>
                                {hasDiscount && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-slate-500">Discount (per cycle)</p>
                                    <p className="text-sm font-medium text-emerald-700" data-testid="view-cycle-discount">−{cs()}{cycleDisc.toFixed(2)}</p>
                                  </div>
                                )}
                              </div>
                              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 mt-1">
                                <div className="flex justify-between items-baseline">
                                  <span className="text-sm font-medium text-emerald-700">Charged per Cycle</span>
                                  <span className="text-2xl font-bold text-emerald-700" data-testid="view-cycle-amount-due">{cs()}{cycleAmtDue.toFixed(2)}</span>
                                </div>
                                <p className="text-[11px] text-emerald-600 mt-0.5">{cycleLabel}{cycleAmtDue === 0 ? ' • FREE' : ''}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                                <div className="space-y-1">
                                  <p className="text-xs text-slate-500">Enrollment Status</p>
                                  <div data-testid="view-status">{getStatusBadge(enrollment.status)}</div>
                                </div>
                                {enrollment.next_billing_date && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-slate-500">Next Bill Date</p>
                                    <p className="text-sm font-medium" data-testid="view-next-bill-date">{formatDate(enrollment.next_billing_date)}</p>
                                  </div>
                                )}
                                {enrollment.is_prorated && (
                                  <>
                                    <div className="space-y-1">
                                      <p className="text-xs text-slate-500">Prorated 1st Month</p>
                                      <p className="text-sm font-medium" data-testid="view-prorated-amount">
                                        {enrollment.prorated_amount != null ? `${cs()}${Number(enrollment.prorated_amount).toFixed(2)}` : 'Yes'}
                                      </p>
                                    </div>
                                    {enrollment.proration_sessions != null && (
                                      <div className="space-y-1">
                                        <p className="text-xs text-slate-500">Proration Sessions</p>
                                        <p className="text-sm font-medium" data-testid="view-proration-sessions">{enrollment.proration_sessions}</p>
                                      </div>
                                    )}
                                  </>
                                )}
                                {(String(enrollment.status || '').toLowerCase() === 'cancelled' ||
                                  String(enrollment.status || '').toLowerCase() === 'canceled' ||
                                  enrollment.end_date) && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-slate-500">End Date</p>
                                    <p className="text-sm font-medium" data-testid="view-end-date">{formatDate(enrollment.end_date) || '-'}</p>
                                  </div>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
                                For payment status, due dates, and per-cycle history,
                                see the <strong>Billing</strong> tab.
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              
              {/* Discount & Proration */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Percent className="h-5 w-5 text-purple-600" />
                    Discount & Proration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    /* iter278: discount editor on the Edit Enrollment
                       page. Catalog dropdown ⇆ free-form amount/reason
                       (mutually exclusive — picking a catalog discount
                       disables the manual fields). `final_monthly_fee`
                       wins over both paths server-side. */
                    <div className="space-y-3" data-testid="ed-discount-section">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-600">Catalog discount</Label>
                        <Select
                          value={editForm.discount_id || 'none'}
                          onValueChange={(v) => {
                            const next = v === 'none' ? '' : v;
                            setEditForm({
                              ...editForm,
                              discount_id: next,
                              discount_amount: next ? '' : editForm.discount_amount,
                              discount_reason: next ? '' : editForm.discount_reason,
                            });
                          }}
                        >
                          <SelectTrigger className="h-9" data-testid="ed-discount-select">
                            <SelectValue placeholder="Select a discount" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No discount</SelectItem>
                            {(discounts || []).map((d) => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name} ({d.value ?? d.discount_value}
                                {d.discount_type === 'percentage' ? '%' : '$'} off)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-600">Custom discount ($)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editForm.discount_amount ?? ''}
                            disabled={!!editForm.discount_id}
                            onChange={(e) => setEditForm({ ...editForm, discount_amount: e.target.value })}
                            placeholder="0.00"
                            className="h-9"
                            data-testid="ed-discount-amount"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-600">Reason</Label>
                          <Input
                            value={editForm.discount_reason ?? ''}
                            disabled={!!editForm.discount_id}
                            onChange={(e) => setEditForm({ ...editForm, discount_reason: e.target.value })}
                            placeholder="e.g. Coach referral"
                            className="h-9"
                            data-testid="ed-discount-reason"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 pt-2 border-t border-purple-200">
                        <Label className="text-xs text-slate-600">
                          {(() => {
                            const mult = Number(enrollment.cycle_multiplier ?? 1);
                            return mult > 1
                              ? `Override final cycle amount ($) — per ${mult}-month cycle`
                              : 'Override final monthly fee ($)';
                          })()}
                          <span className="ml-1 text-slate-400">— wins over the discount above</span>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.final_monthly_fee ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, final_monthly_fee: e.target.value })}
                          placeholder={(() => {
                            const mult = Number(enrollment.cycle_multiplier ?? 1);
                            const baseFee = Number(enrollment.base_monthly_fee ?? enrollment.monthly_fee ?? 0);
                            const cycleTotal = baseFee * mult;
                            if (mult > 1 && cycleTotal > 0) {
                              return `e.g. ${cycleTotal.toFixed(2)} (= ${cs()}${baseFee.toFixed(2)} × ${mult})`;
                            }
                            return 'Leave blank to use monthly fee minus discount';
                          })()}
                          className="h-9"
                          data-testid="ed-final-fee"
                        />
                        {Number(enrollment.cycle_multiplier ?? 1) > 1 && (
                          <p className="text-[11px] text-slate-500 mt-1">
                            Enter the total amount per <strong>{enrollment.cycle_multiplier}-month</strong> cycle.
                            We&apos;ll store it as <code>monthly_fee = value ÷ {enrollment.cycle_multiplier}</code> internally.
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Saving updates next invoice + recurring cycle. A `discount_usage`
                        audit row is written automatically.
                      </p>
                    </div>
                  ) : (() => {
                    // iter307cr — RIGHT card = discount METADATA only.
                    // Dollar amounts (cycle total, discount $, charged
                    // per cycle) live in the Payment Details card on
                    // the left so we never duplicate or contradict.
                    const meta = enrollment.discount_meta || {};
                    const name =
                      enrollment.discount_name
                      || meta.discount_name
                      || enrollment.discount_reason
                      || (enrollment.discount_id ? 'Discount' : null);
                    const cycleDisc = Number(enrollment.cycle_discount_amount ?? 0);
                    const original = Number(enrollment.original_monthly_fee ?? 0);
                    const current = Number(enrollment.monthly_fee ?? 0);
                    const derivedSavings = original > current ? original - current : 0;
                    const hasDiscount =
                      !!enrollment.discount_id
                      || cycleDisc > 0
                      || derivedSavings > 0
                      || !!enrollment.discount_reason;
                    if (!hasDiscount) {
                      return <p className="text-sm text-slate-500" data-testid="enrollment-no-discount">No discount applied</p>;
                    }
                    const isPercentage = meta.discount_type === 'percentage';
                    const isFixed = meta.discount_type === 'fixed';
                    const valueLabel =
                      isPercentage && meta.discount_value != null
                        ? `${meta.discount_value}%`
                        : isFixed && meta.discount_value != null
                        ? `${cs()}${Number(meta.discount_value).toFixed(2)} off`
                        : null;
                    const typeLabel = isPercentage ? 'Percentage' : isFixed ? 'Fixed Amount' : 'Custom';
                    return (
                      <div className="p-3 bg-purple-50 rounded-lg space-y-2" data-testid="enrollment-discount-panel">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-purple-600">Discount Applied</span>
                          <Badge className="bg-purple-100 text-purple-700">
                            {name || 'Discount'}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-purple-600">Type</span>
                          <span className="text-sm font-medium text-slate-700">{typeLabel}</span>
                        </div>
                        {valueLabel && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-purple-600">Value</span>
                            <span className="text-sm font-semibold text-purple-700">{valueLabel}</span>
                          </div>
                        )}
                        {enrollment.discount_reason && enrollment.discount_reason !== name && (
                          <div className="flex justify-between items-start gap-3 text-xs pt-1 border-t border-purple-200">
                            <span className="text-purple-500 whitespace-nowrap">Reason</span>
                            <span className="text-slate-700 text-right">{enrollment.discount_reason}</span>
                          </div>
                        )}
                        <p className="text-[11px] text-purple-500 pt-1 border-t border-purple-200">
                          Dollar amounts → see <strong>Payment Details</strong> card on the left.
                        </p>
                      </div>
                    );
                  })()}
                  
                  {enrollment.is_prorated && (
                    <div className="p-3 bg-amber-50 rounded-lg mt-4">
                      <div className="text-sm font-medium text-amber-700 mb-2">Prorated First Month</div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-amber-600">Sessions</span>
                        <span>{enrollment.proration_sessions} / {enrollment.total_sessions}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm text-amber-600">Prorated Amount</span>
                        <span className="font-medium">{cs()}{enrollment.prorated_amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  {enrollment.credit_balance > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-600">Credit Balance</span>
                        <span className="font-bold text-green-700">{cs()}{enrollment.credit_balance?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Subscription Info */}
              {subscription && (
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CreditCard className="h-5 w-5 text-indigo-600" />
                      Subscription
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500 mb-1">Status</div>
                        <Badge className={subscription.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {subscription.status}
                        </Badge>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500 mb-1">Monthly Amount</div>
                        <div className="font-medium">{cs()}{subscription.monthly_amount?.toFixed(2)}</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500 mb-1">Next Billing</div>
                        <div className="font-medium">{formatDate(subscription.next_billing_date)}</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500 mb-1">Credit Balance</div>
                        <div className="font-medium">{cs()}{subscription.credit_balance?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* iter300: Notes (moved from above the merged billing
                cards to keep the billing summary close to the
                Important Dates section per user spec). */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <Textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Add notes about this enrollment..."
                    rows={4}
                  />
                ) : (
                  <p className="text-slate-600">
                    {enrollment.notes || 'No notes added'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* iter307 — Billing Tab: per-cycle ledger pulled from monthly_invoices */}
          <TabsContent value="billing" className="space-y-6">
            <EnrollmentBillingTab enrollmentId={enrollmentId} />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            {/* iter300: Pause / Cancel requests rendered as historical
                rows with admin decision metadata. Surfaces both pending
                and acted-on requests with status badge + decided-by +
                decision date + computed credit. */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Pause className="h-5 w-5 text-amber-500" />
                  Pause / Cancel Requests
                  <span className="text-sm font-normal text-slate-500">({requests?.length || 0})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {requests && requests.length > 0 ? (
                  <div className="overflow-x-auto" data-testid="enrollment-requests-table">
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs text-slate-500 uppercase border-b">
                        <tr>
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Type</th>
                          <th className="py-2 pr-3">Reason</th>
                          {/* iter303: pause window dates surface
                              directly in the table so admins don't
                              have to drill into each row. */}
                          <th className="py-2 pr-3">Pause Start</th>
                          <th className="py-2 pr-3">Resume</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Decided By</th>
                          <th className="py-2 pr-3">Decision Date</th>
                          <th className="py-2 pr-3 text-right">Credit / Refund</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {requests.map((req) => {
                          const statusClass = {
                            pending: 'bg-amber-100 text-amber-700',
                            approved: 'bg-green-100 text-green-700',
                            rejected: 'bg-rose-100 text-rose-700',
                            cancelled: 'bg-slate-100 text-slate-700',
                          }[req.status] || 'bg-slate-100 text-slate-700';
                          const typeLabel = req.type === 'pause' ? 'Pause' : req.type === 'cancel' ? 'Cancel' : (req.type || '—');
                          return (
                            <tr key={req.id} className="hover:bg-slate-50" data-testid={`request-row-${req.id}`}>
                              <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">
                                {req.created_at ? formatDate(req.created_at) : '—'}
                              </td>
                              <td className="py-2 pr-3 font-medium text-slate-800">{typeLabel}</td>
                              <td className="py-2 pr-3 text-slate-600 max-w-[220px] truncate" title={req.reason || ''}>
                                {req.reason || <span className="text-slate-400">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-slate-600 whitespace-nowrap" data-testid={`pause-start-${req.id}`}>
                                {req.pause_start_date ? formatDate(req.pause_start_date) : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-slate-600 whitespace-nowrap" data-testid={`resume-date-${req.id}`}>
                                {req.resume_date
                                  ? formatDate(req.resume_date)
                                  : req.cancel_date
                                    ? formatDate(req.cancel_date)
                                    : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="py-2 pr-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
                                  {req.status || 'pending'}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-slate-600">{req.decided_by_name || (req.status === 'pending' ? '—' : '—')}</td>
                              <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                                {req.decision_at ? formatDate(req.decision_at) : '—'}
                              </td>
                              <td className="py-2 pr-3 text-right font-medium text-slate-800 whitespace-nowrap">
                                {req.credit_amount != null && Number(req.credit_amount) > 0
                                  ? `${cs()}${Number(req.credit_amount).toFixed(2)}`
                                  : <span className="text-slate-400">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4">No pause or cancellation requests on file.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="h-5 w-5 text-slate-600" />
                  Enrollment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history && history.length > 0 ? (
                  <div className="space-y-4">
                    {history.map((entry, idx) => (
                      <div 
                        key={entry.id || idx} 
                        className="flex gap-4 p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex-shrink-0 mt-1">
                          {getActionIcon(entry.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{formatActionName(entry.action)}</span>
                            <span className="text-sm text-slate-400">
                              {entry.old_status} → {entry.new_status}
                            </span>
                          </div>
                          {entry.admin_name && (
                            <div className="text-sm text-slate-500 mt-1">
                              by {entry.admin_name}
                            </div>
                          )}
                          {entry.notes && (
                            <div className="text-sm text-slate-600 mt-2 p-2 bg-slate-100 rounded">
                              {entry.notes}
                            </div>
                          )}
                          {entry.details && Object.keys(entry.details).length > 0 && (
                            <div className="text-xs text-slate-500 mt-2">
                              {entry.details.changes && (
                                <div>
                                  {Object.entries(entry.details.changes).map(([field, change]) => (
                                    <span key={field} className="mr-2">
                                      {field}: {String(change.old)} → {String(change.new)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No history records found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Quick Actions Dialog */}
        <Dialog open={actionDialog} onOpenChange={setActionDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-action-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {currentAction === 'pause' && <PauseCircle className="h-5 w-5 text-amber-500" />}
                {currentAction === 'cancel' && <XCircle className="h-5 w-5 text-red-500" />}
                {currentAction === 'resume' && <PlayCircle className="h-5 w-5 text-green-500" />}
                {currentAction === 'move' && <ArrowRightLeft className="h-5 w-5 text-blue-500" />}
                {currentAction === 'schedule_cancel' && <CalendarClock className="h-5 w-5 text-orange-500" />}
                {currentAction === 'remove_schedule_cancel' && <X className="h-5 w-5 text-slate-500" />}
                {currentAction === 'pause' && 'Pause Enrollment'}
                {currentAction === 'cancel' && 'Cancel Enrollment'}
                {currentAction === 'resume' && 'Resume Enrollment'}
                {currentAction === 'move' && 'Move to Another Class'}
                {currentAction === 'schedule_cancel' && 'Schedule Future Cancellation'}
                {currentAction === 'remove_schedule_cancel' && 'Remove Scheduled Cancellation'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Enrollment Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{program?.name}</p>
                <p className="text-sm text-slate-500">
                  {enrollmentClasses?.map(c => c.name).join(', ') || 'No class assigned'}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span>Fee: {cs()}{enrollment?.monthly_fee}/mo</span>
                  <span className="capitalize">({enrollment?.payment_frequency})</span>
                </div>
                {enrollment?.credit_balance > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    Existing Credit: {cs()}{enrollment.credit_balance.toFixed(2)}
                  </p>
                )}
              </div>
              
              {/* Pause Form */}
              {currentAction === 'pause' && (
                <div className="space-y-4">
                  {/* iter304: pause-already-in-place warning. Fires
                       whenever an active or scheduled pause exists on
                       the enrollment, OR when an admin-action history
                       row exists for a pause whose resume_date is in
                       the future (covers iter300 admin-direct pauses
                       which don't sit in the requests collection),
                       OR when there's a pending pause request. */}
                  {(() => {
                    // Find an active in-flight pause if any.
                    const todayIso = new Date().toISOString().slice(0, 10);
                    const inflight = (requests || []).find((r) => {
                      if (r.type !== 'pause') return false;
                      if (r.status === 'pending') return true;
                      if (r.status === 'approved' && r.resume_date && String(r.resume_date).slice(0, 10) >= todayIso) {
                        return true;
                      }
                      return false;
                    });
                    const existingActive =
                      enrollment?.status === 'paused' ||
                      enrollment?.scheduled_pause_date ||
                      (enrollment?.pause_date && enrollment?.auto_resume_date);
                    if (!inflight && !existingActive) return null;

                    const startDate = (
                      inflight?.pause_start_date ||
                      enrollment?.pause_date ||
                      enrollment?.scheduled_pause_date
                    );
                    const resumeDate = inflight?.resume_date || enrollment?.auto_resume_date;
                    const decidedBy = inflight?.decided_by_name;
                    const reason = inflight?.reason || enrollment?.pause_reason;
                    const headline = enrollment?.status === 'paused'
                      ? 'Enrollment is already paused'
                      : (inflight?.status === 'pending'
                          ? 'A pause request is pending review'
                          : 'A pause is already scheduled');
                    return (
                      <div
                        className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
                        data-testid="active-pause-warning"
                      >
                        <PauseCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-semibold mb-1">{headline}</div>
                          <div className="space-y-0.5 text-amber-800">
                            {startDate && (
                              <div>
                                <span className="text-amber-600">Pause start:</span>{' '}
                                <span className="font-medium">{formatDate(startDate)}</span>
                              </div>
                            )}
                            {resumeDate && (
                              <div>
                                <span className="text-amber-600">Resume:</span>{' '}
                                <span className="font-medium">{formatDate(resumeDate)}</span>
                              </div>
                            )}
                            {reason && (
                              <div>
                                <span className="text-amber-600">Reason:</span>{' '}
                                <span className="font-medium">{reason}</span>
                              </div>
                            )}
                            {decidedBy && (
                              <div>
                                <span className="text-amber-600">Created by:</span>{' '}
                                <span className="font-medium">{decidedBy}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-amber-700 mt-2">
                            Submitting a new pause will overwrite these dates.
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* iter300: surface the dates that drive billing so
                       admin can see them on the same screen as the
                       pause window. Start Date stays read-only;
                       Next Bill Date auto-recomputes from the pause
                       duration but can be overridden inline. */}
                  <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-500">Enrollment Start Date</Label>
                      <div className="text-sm font-medium text-slate-700 mt-1" data-testid="pause-enrollment-start-date">
                        {enrollment?.start_date ? formatDate(enrollment.start_date) : '—'}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Current Next Bill Date</Label>
                      <div className="text-sm font-medium text-slate-700 mt-1" data-testid="pause-current-nbd">
                        {enrollment?.next_billing_date ? formatDate(enrollment.next_billing_date) : '—'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pause Start Date *</Label>
                      <Input
                        type="date"
                        value={actionForm.pause_start_date}
                        onChange={(e) => {
                          setActionForm({
                            ...actionForm,
                            pause_start_date: e.target.value,
                            // Re-arm auto-fill: any subsequent change to
                            // pause dates should refresh the suggested
                            // next billing date.
                            next_billing_date_touched: false,
                          });
                          setCreditCalculation(null);
                        }}
                        data-testid="pause-start-date"
                      />
                      {/* iter334ba — Backdating allowed. Credit auto-
                          calculated below whenever pause_start_date
                          changes. */}
                      <p className="text-[11px] text-slate-500">
                        Backdate allowed — credit for any unused portion is auto-calculated below.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Resume Date{' '}
                        <span className="text-slate-400 text-xs font-normal">(optional)</span>
                      </Label>
                      <Input
                        type="date"
                        value={actionForm.resume_date}
                        onChange={(e) =>
                          setActionForm({
                            ...actionForm,
                            resume_date: e.target.value,
                            next_billing_date_touched: false,
                          })
                        }
                        min={actionForm.pause_start_date}
                        data-testid="pause-resume-date"
                      />
                      <p className="text-[11px] text-slate-500">
                        Leave blank to pause indefinitely — use <strong>Resume</strong> later.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center justify-between">
                      <span>New Next Bill Date (after pause)</span>
                      {actionForm.next_billing_date_touched && computedPostPauseNbd && (
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:underline"
                          onClick={() =>
                            setActionForm({
                              ...actionForm,
                              next_billing_date: computedPostPauseNbd,
                              next_billing_date_touched: false,
                            })
                          }
                          data-testid="reset-computed-nbd"
                        >
                          Reset to auto-computed
                        </button>
                      )}
                    </Label>
                    <Input
                      type="date"
                      value={actionForm.next_billing_date}
                      onChange={(e) =>
                        setActionForm({
                          ...actionForm,
                          next_billing_date: e.target.value,
                          next_billing_date_touched: true,
                        })
                      }
                      data-testid="pause-next-billing-date"
                    />
                    <p className="text-xs text-slate-500">
                      Auto-shifted forward by the pause duration (rounded to whole months).
                      Override if you want a different post-pause cycle start.
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={calculatePauseCredit}
                    disabled={calculatingCredit || !actionForm.pause_start_date}
                    data-testid="calculate-pause-credit"
                  >
                    {calculatingCredit ? 'Calculating...' : 'Calculate Credit'}
                  </Button>

                  {/* iter334bb — Zero-credit override for pause. Only
                      surfaces when the calculated credit is > 0 so an
                      empty pause (unpaid month, end-of-cycle) doesn't
                      show a misleading control. */}
                  {creditCalculation && (creditCalculation.credit_amount || 0) > 0 && (
                    <label
                      className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50 border-slate-200 cursor-pointer"
                      data-testid="pause-zero-credit-block"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 text-slate-700 rounded"
                        checked={!!actionForm.override_credit}
                        onChange={(e) =>
                          setActionForm({ ...actionForm, override_credit: e.target.checked })
                        }
                        data-testid="pause-zero-credit-checkbox"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-medium">Do not issue credit (zero out the {cs()}{creditCalculation.credit_amount.toFixed(2)} above)</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Use this when the pause is mutually agreed and no refund / account credit should be issued.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}
              
              {/* Cancel Form */}
              {currentAction === 'cancel' && (
                <div className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Notice Period Required</p>
                        <p className="text-amber-700">
                          Cancellation requires 1 billing cycle notice (minimum 30 days).
                          {creditCalculation?.earliest_cancel_date && (
                            <span className="block mt-1">
                              Earliest cancel date: <strong>{formatDate(creditCalculation.earliest_cancel_date)}</strong>
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Cancel Date</Label>
                    <Input
                      type="date"
                      value={actionForm.cancel_date}
                      onChange={(e) => {
                        setActionForm({ ...actionForm, cancel_date: e.target.value });
                        if (e.target.value) {
                          calculateCancelCredit(e.target.value);
                        }
                      }}
                      data-testid="cancel-date"
                    />
                    <p className="text-xs text-slate-500">
                      Admin can pick any date — leave empty to use the earliest allowed date.
                    </p>
                  </div>

                  {/* Early-cancel warning + admin override */}
                  {actionForm.cancel_date && creditCalculation?.earliest_cancel_date && (
                    new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date)
                  ) && (
                    <div
                      className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2"
                      data-testid="early-cancel-warning"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-red-800">
                          <p className="font-medium">Cancelling BEFORE the notice period</p>
                          <p className="text-red-700 mt-1">
                            The selected date is earlier than the 30-day notice + current cycle.
                            This bypasses the standard cancellation policy and is logged in the audit trail.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-red-900 pl-7 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!actionForm.override_notice_period}
                          onChange={(e) =>
                            setActionForm({ ...actionForm, override_notice_period: e.target.checked })
                          }
                          data-testid="override-notice-checkbox"
                        />
                        <span>I confirm overriding the notice period (admin)</span>
                      </label>
                    </div>
                  )}

                  {/* Zero-credit override */}
                  {creditCalculation && (creditCalculation.credit_amount || 0) > 0 && (
                    <label
                      className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50 border-slate-200 cursor-pointer"
                      data-testid="zero-credit-block"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 text-slate-700 rounded"
                        checked={!!actionForm.override_credit}
                        onChange={(e) =>
                          setActionForm({ ...actionForm, override_credit: e.target.checked })
                        }
                        data-testid="zero-credit-checkbox"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-medium">Do not issue credit (zero out the {cs()}{creditCalculation.credit_amount.toFixed(2)} above)</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Use this when the cancellation is mutually agreed and no refund / account credit should be issued.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}
              
              {/* Resume Form */}
              {currentAction === 'resume' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Resume Date</Label>
                    <Input
                      type="date"
                      value={actionForm.resume_date}
                      onChange={(e) => setActionForm({ ...actionForm, resume_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="resume-date"
                    />
                    <p className="text-xs text-slate-500">
                      Leave empty to resume immediately
                    </p>
                  </div>
                </div>
              )}
              
              {/* Move Form */}
              {currentAction === 'move' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select New Class *</Label>
                    <Select
                      value={actionForm.new_class_id}
                      onValueChange={(v) => setActionForm({ ...actionForm, new_class_id: v })}
                    >
                      <SelectTrigger data-testid="move-class-select">
                        <SelectValue placeholder="Select a class" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableClasses.length === 0 ? (
                          <SelectItem value="none" disabled>No other classes available</SelectItem>
                        ) : (
                          availableClasses.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>
                              {cls.name} - {cls.schedule_day || cls.day_of_week} {cls.start_time}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {/* Schedule Cancel Form */}
              {currentAction === 'schedule_cancel' && (
                <div className="space-y-4">
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                      <div className="text-sm text-orange-700">
                        <p className="font-medium">Schedule a Future Cancellation</p>
                        <p className="mt-1">
                          The enrollment will automatically cancel on the selected date. 
                          Billing will stop on this date.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Billing Cycle Policy Notice */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Cancellation Policy</p>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        <span>Billing cycles start on the <strong>1st of each month</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        <span>Cancellations can only occur on the <strong>last day of a month</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        <span>At least <strong>one full billing cycle notice</strong> is required</span>
                      </li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2 italic">
                      Example: If today is March 22nd, the earliest cancellation date is April 30th.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Cancellation Date *</Label>
                    <Input
                      type="date"
                      value={actionForm.scheduled_cancel_date}
                      onChange={(e) => setActionForm({ ...actionForm, scheduled_cancel_date: e.target.value })}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      data-testid="scheduled-cancel-date"
                    />
                    <p className="text-xs text-slate-500">
                      Select the last day of a month. Parent will receive an email notification immediately and a reminder 3 days before cancellation.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason (Optional)</Label>
                    <Textarea
                      value={actionForm.reason}
                      onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                      placeholder="Reason for scheduling cancellation..."
                      data-testid="schedule-cancel-reason"
                    />
                  </div>
                </div>
              )}
              
              {/* Remove Scheduled Cancel Confirmation */}
              {currentAction === 'remove_schedule_cancel' && (
                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-700">
                      The scheduled cancellation for <strong>{data?.enrollment?.scheduled_cancel_date}</strong> will be removed. 
                      The enrollment will continue as normal.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Credit Calculation Display */}
              {creditCalculation && (currentAction === 'pause' || currentAction === 'cancel') && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <DollarSign className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800">Credit Calculation</p>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>Billing Period: {formatDate(creditCalculation.billing_period_start)} - {formatDate(creditCalculation.billing_period_end)}</p>
                        <p>Days Remaining: {creditCalculation.days_remaining} of {creditCalculation.days_in_period}</p>
                        <p className="font-medium text-lg">
                          Credit: {cs()}{creditCalculation.credit_amount?.toFixed(2) || '0.00'}
                        </p>
                        {creditCalculation.is_end_of_cycle && (
                          <p className="text-green-600">At end of billing cycle - no credit needed</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reason — Mandatory on cancel; optional on other actions
                  that don't have their own reason field. */}
              {currentAction !== 'schedule_cancel' && currentAction !== 'remove_schedule_cancel' && (
                <div className="space-y-2">
                  <Label>
                    Reason
                    {currentAction === 'cancel' ? (
                      <span className="text-red-500 ml-1">*</span>
                    ) : (
                      <span className="text-slate-400 text-xs"> (Optional)</span>
                    )}
                  </Label>
                  <Textarea
                    value={actionForm.reason}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    placeholder={currentAction === 'cancel'
                      ? 'Required — record why this enrollment is being cancelled.'
                      : 'Enter reason for this action...'}
                    rows={2}
                    required={currentAction === 'cancel'}
                    data-testid="action-reason"
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleQuickAction}
                disabled={
                  processingAction ||
                  (currentAction === 'cancel' && (
                    !(actionForm.reason || '').trim() ||
                    (
                      actionForm.cancel_date &&
                      creditCalculation?.earliest_cancel_date &&
                      new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date) &&
                      !actionForm.override_notice_period
                    )
                  ))
                }
                className={
                  currentAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' :
                  currentAction === 'pause' ? 'bg-amber-600 hover:bg-amber-700' :
                  currentAction === 'move' ? 'bg-blue-600 hover:bg-blue-700' :
                  currentAction === 'schedule_cancel' ? 'bg-orange-600 hover:bg-orange-700' :
                  currentAction === 'remove_schedule_cancel' ? 'bg-slate-600 hover:bg-slate-700' :
                  'bg-green-600 hover:bg-green-700'
                }
                data-testid="confirm-action-btn"
              >
                {processingAction ? 'Processing...' : 
                  currentAction === 'pause' ? 'Pause Enrollment' :
                  currentAction === 'cancel' ? 'Cancel Enrollment' :
                  currentAction === 'move' ? 'Move Enrollment' :
                  currentAction === 'schedule_cancel' ? 'Schedule Cancellation' :
                  currentAction === 'remove_schedule_cancel' ? 'Remove Scheduled Cancel' :
                  'Resume Enrollment'
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EnrollmentDetail;
